import React from "react";
import { Chip, IconButton, Box, Tooltip } from "@mui/material";
import { DeleteActionIcon, EditActionIcon } from "@components/common/Icons/ActionIcons";
import { themeVar } from "@config/theme/cssVar";
import type { Equipment } from "../../../../EquipmentsTab/types/types";
import { truncateEquipmentName } from "../../../../EquipmentsTab/utils/equipmentUtils";

interface EquipmentChipProps {
	item: {
		equipment: Equipment;
		count: number;
	};
	index: number;
	isEditing: boolean;
	isHovered: boolean;
	isSubmitting: boolean;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onStartEdit: (index: number) => void;
	onRemoveEquipment: (index: number) => void;
}

const EquipmentChip: React.FC<EquipmentChipProps> = ({
	item,
	index,
	isEditing,
	isHovered,
	isSubmitting,
	onMouseEnter,
	onMouseLeave,
	onStartEdit,
	onRemoveEquipment,
}) => {
	// Format equipment display text with truncation
	const formatEquipmentDisplay = () => {
		const equipmentLabel = item.equipment.label || "";
		const truncatedName = truncateEquipmentName(equipmentLabel, 20); // Truncate at 20 characters for chips
		const displayText = `${truncatedName} (${item.count} units)`;

		// If name was truncated, show full name in tooltip
		if (equipmentLabel.length > 20) {
			const fullText = `${equipmentLabel} (${item.count} units)`;
			return (
				<Tooltip title={fullText} arrow placement="top">
					<span className="flex-1">{displayText}</span>
				</Tooltip>
			);
		}

		// If name wasn't truncated, just return the text
		return <span className="flex-1">{displayText}</span>;
	};

	return (
		<Box
			className="relative inline-block"
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			sx={{
				"&:hover .equipment-chip": {
					boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
					transform: "translateY(-1px)",
				},
			}}>
			<Chip
				label={
					<div className="flex items-center gap-2">
						{formatEquipmentDisplay()}
						{isHovered && !isEditing && (
							<div className="flex gap-1 ml-2">
								<IconButton
									size="small"
									onClick={e => {
										e.stopPropagation();
										onStartEdit(index);
									}}
									disabled={isSubmitting}
									sx={{
										width: 20,
										height: 20,
										minWidth: 20,
										marginTop: 0,
										backgroundColor: themeVar.actionEditSurface,
										color: themeVar.actionEdit,
										boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
										"&:hover": {
											backgroundColor: themeVar.actionEditSurface,
											transform: "scale(1.05)",
										},
										"&:disabled": {
											backgroundColor: themeVar.disabledBackground,
											color: themeVar.muted,
										},
									}}>
									<EditActionIcon sx={{ fontSize: 14 }} />
								</IconButton>
								<IconButton
									size="small"
									onClick={e => {
										e.stopPropagation();
										onRemoveEquipment(index);
									}}
									disabled={isSubmitting}
									sx={{
										width: 20,
										height: 20,
										minWidth: 20,
										marginTop: 0,
										backgroundColor: "var(--admin-status-danger-surface)",
										color: themeVar.danger,
										boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
										"&:hover": {
											backgroundColor: "var(--admin-status-danger-surface)",
											transform: "scale(1.05)",
										},
										"&:disabled": {
											backgroundColor: themeVar.disabledBackground,
											color: themeVar.muted,
										},
									}}>
									<DeleteActionIcon sx={{ fontSize: 14 }} />
								</IconButton>
							</div>
						)}
						{/* Show only remove button when editing */}
						{isHovered && isEditing && (
							<div className="flex gap-1 ml-2">
								<IconButton
									size="small"
									onClick={e => {
										e.stopPropagation();
										onRemoveEquipment(index);
									}}
									disabled={isSubmitting}
									sx={{
										width: 20,
										height: 20,
										minWidth: 20,
										marginTop: 0,
										backgroundColor: "var(--admin-status-danger-surface)",
										color: themeVar.danger,
										boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
										"&:hover": {
											backgroundColor: "var(--admin-status-danger-surface)",
											transform: "scale(1.05)",
										},
										"&:disabled": {
											backgroundColor: themeVar.disabledBackground,
											color: themeVar.muted,
										},
									}}>
									<DeleteActionIcon sx={{ fontSize: 14 }} />
								</IconButton>
							</div>
						)}
					</div>
				}
				className={`equipment-chip cursor-pointer transition-all duration-200 ${
					isEditing
						? "bg-gradient-to-r from-amber-100 to-orange-200 text-amber-900 border-2 border-amber-400 shadow-md"
						: isHovered
							? "bg-gradient-to-r from-sky-200 to-blue-300 text-sky-900 border-2 border-sky-400 shadow-md"
							: "bg-gradient-to-r from-emerald-50 to-teal-100 text-emerald-800 border border-emerald-200 shadow-sm"
				}`}
				sx={{
					position: "relative",
					zIndex: 1,
					"& .MuiChip-label": {
						padding: "8px 12px",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						width: "100%",
					},
					"&:hover": {
						background: isEditing
							? "linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%)"
							: "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)",
						border: isEditing ? "2px solid #f59e0b" : "2px solid #0ea5e9",
						boxShadow: isEditing ? "0 2px 8px rgba(245, 158, 11, 0.15)" : "0 2px 8px rgba(14, 165, 233, 0.15)",
					},
				}}
				onClick={() => onStartEdit(index)}
			/>

			{/* Editing indicator */}
			{isEditing && (
				<Box
					className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg"
					sx={{
						zIndex: 2,
						animation: "pulse 2s infinite",
						"@keyframes pulse": {
							"0%": {
								boxShadow: "0 0 0 0 rgba(245, 158, 11, 0.7)",
							},
							"70%": {
								boxShadow: "0 0 0 10px rgba(245, 158, 11, 0)",
							},
							"100%": {
								boxShadow: "0 0 0 0 rgba(245, 158, 11, 0)",
							},
						},
					}}>
					<EditActionIcon fontSize="small" />
				</Box>
			)}

			{/* Hover glow effect */}
			{isHovered && !isEditing && (
				<Box
					className="absolute inset-0 rounded-full"
					sx={{
						background: "linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)",
						zIndex: 0,
						animation: "glow 1.5s ease-in-out infinite alternate",
						"@keyframes glow": {
							from: {
								boxShadow: "0 0 5px rgba(14, 165, 233, 0.3)",
							},
							to: {
								boxShadow: "0 0 20px rgba(14, 165, 233, 0.6)",
							},
						},
					}}
				/>
			)}

			{/* Editing glow effect */}
			{isHovered && isEditing && (
				<Box
					className="absolute inset-0 rounded-full"
					sx={{
						background: "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 146, 60, 0.1) 100%)",
						zIndex: 0,
						animation: "glowOrange 1.5s ease-in-out infinite alternate",
						"@keyframes glowOrange": {
							from: {
								boxShadow: "0 0 5px rgba(245, 158, 11, 0.3)",
							},
							to: {
								boxShadow: "0 0 20px rgba(245, 158, 11, 0.6)",
							},
						},
					}}
				/>
			)}

			{/* Default state subtle glow */}
			{!isHovered && !isEditing && (
				<Box
					className="absolute inset-0 rounded-full"
					sx={{
						background: "linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(20, 184, 166, 0.05) 100%)",
						zIndex: 0,
						animation: "subtleGlow 3s ease-in-out infinite alternate",
						"@keyframes subtleGlow": {
							from: {
								boxShadow: "0 0 3px rgba(16, 185, 129, 0.1)",
							},
							to: {
								boxShadow: "0 0 8px rgba(16, 185, 129, 0.2)",
							},
						},
					}}
				/>
			)}
		</Box>
	);
};

export default EquipmentChip;
